/**
 * Passenger form validation for seat booking.
 * Returns empty string when valid, otherwise a user-facing message.
 */

const NAME_RE = /^[A-Za-z][A-Za-z\s'.-]*$/;
/** Digits with optional leading +, spaces/hyphens/parentheses allowed while typing. */
const MOBILE_CHARS_RE = /^[+\d\s()-]*$/;
const MOBILE_NORMALIZED_RE = /^\+?\d{9,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validatePassengerName(value, label = "Name") {
  const v = String(value ?? "").trim();
  if (!v) return `${label} is required`;
  if (/\d/.test(v)) return `${label} cannot contain numbers`;
  if (!NAME_RE.test(v)) {
    return `${label} may only contain letters, spaces, hyphen, apostrophe`;
  }
  if (v.length < 2) return `${label} must be at least 2 characters`;
  return "";
}

export function validatePassengerMobile(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "Mobile number is required";
  if (!MOBILE_CHARS_RE.test(raw)) {
    return "Mobile number may only contain digits and + (spaces or - allowed)";
  }
  const digits = raw.replace(/[^\d+]/g, "");
  if ((digits.match(/\+/g) || []).length > 1 || (digits.includes("+") && !digits.startsWith("+"))) {
    return "Use + only at the start of the mobile number (e.g. +94701234567)";
  }
  const normalized = digits.replace(/\s/g, "");
  if (!MOBILE_NORMALIZED_RE.test(normalized)) {
    return "Enter a valid mobile number (9–15 digits, optional + country code)";
  }
  return "";
}

export function validatePassengerEmail(value, { required = true } = {}) {
  const v = String(value ?? "").trim();
  if (!v) return required ? "Email is required" : "";
  if (/\s/.test(v)) return "Email cannot contain spaces";
  if (!EMAIL_RE.test(v)) return "Enter a valid email address (e.g. name@example.com)";
  return "";
}

/**
 * @returns {{ ok: boolean, errors: { first_name?: string, last_name?: string, mobile_no?: string, email?: string } }}
 */
export function validatePassengerDetails(details = {}, { emailRequired = true } = {}) {
  const errors = {
    first_name: validatePassengerName(details.first_name, "First name"),
    last_name: validatePassengerName(details.last_name, "Last name"),
    mobile_no: validatePassengerMobile(details.mobile_no),
    email: validatePassengerEmail(details.email, { required: emailRequired }),
  };
  const cleaned = Object.fromEntries(
    Object.entries(errors).filter(([, msg]) => Boolean(msg)),
  );
  return { ok: Object.keys(cleaned).length === 0, errors: cleaned };
}
